import React, { useState, useEffect } from "react";
import DOMPurify from 'dompurify';
import { FaEdit, FaEye, FaTrash, FaSave, FaTimes, FaToggleOn, FaToggleOff, FaHistory, FaDownload } from "react-icons/fa";
import { FiRefreshCw } from "react-icons/fi";

const ContentManagement = () => {
  const [terms, setTerms] = useState("");
  const [privacyPolicy, setPrivacyPolicy] = useState("");
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState({});
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  
  // Modal states
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewContent, setPreviewContent] = useState({ type: '', content: '', title: '' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Document metadata
  const [documentsInfo, setDocumentsInfo] = useState({
    terms_conditions: { lastUpdated: null, status: true },
    privacy_policy: { lastUpdated: null, status: true }
  });

  const API_BASE_URL = `${process.env.REACT_APP_API_URL}/api/admin/admin-document` || "https://api.bookwie.com/api/admin/admin-document";
  const token = localStorage.getItem("token");

  // Fetch data from API
  const fetchData = async (type, setter) => {
    try {
      const response = await fetch(`${API_BASE_URL}/${type}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setter(data.data.content || "");
          setDocumentsInfo(prev => ({
            ...prev,
            [type]: {
              lastUpdated: data.data.lastUpdated || data.data.updatedAt,
              status: data.data.status !== undefined ? data.data.status : true
            }
          }));
        }
      } else if (response.status === 404) {
        // Document doesn't exist yet
        setter("");
      }
    } catch (error) {
      console.error(`Error fetching ${type}:`, error);
      setUploadError(`Failed to fetch ${type.replace('_', ' ')}`);
    }
  };

  // Fetch all documents
  const fetchAllDocuments = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchData("terms_conditions", setTerms),
        fetchData("privacy_policy", setPrivacyPolicy)
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllDocuments();
  }, []);

  // Save or Update data
  const handleSave = async (type, content) => {
    if (!content.trim()) {
      setUploadError("Content cannot be empty");
      return;
    }

    setSaveLoading({ [type]: true });
    setUploadError(null);
    setUploadSuccess(null);

    try {
      const response = await fetch(API_BASE_URL, {
        method: "POST",
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ type, content })
      });

      const data = await response.json();

      if (data.success) {
        setUploadSuccess(`${type.replace("_", " ")} saved successfully!`);
        // Update document info
        setDocumentsInfo(prev => ({
          ...prev,
          [type]: {
            lastUpdated: new Date().toISOString(),
            status: true
          }
        }));
        
        // Clear success message after 3 seconds
        setTimeout(() => setUploadSuccess(null), 3000);
      } else {
        setUploadError(data.message || "Error saving data.");
      }
    } catch (error) {
      console.error(`Error saving ${type}:`, error);
      setUploadError("Failed to save data.");
    } finally {
      setSaveLoading({});
    }
  };

  // Delete data
  const handleDelete = async (type, setter) => {
    try {
      const response = await fetch(`${API_BASE_URL}/${type}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();

      if (data.success) {
        setter("");
        setDocumentsInfo(prev => ({
          ...prev,
          [type]: { lastUpdated: null, status: true }
        }));
        setUploadSuccess(`${type.replace("_", " ")} deleted successfully.`);
        setTimeout(() => setUploadSuccess(null), 3000);
      } else {
        setUploadError(data.message || "Error deleting data.");
      }
    } catch (error) {
      console.error(`Error deleting ${type}:`, error);
      setUploadError("Failed to delete data.");
    } finally {
      setDeleteConfirm(null);
    }
  };

  // Preview content
  const handlePreview = (type, content) => {
    const title = type === 'terms_conditions' ? 'Terms & Conditions' : 'Privacy Policy';
    setPreviewContent({ type, content, title });
    setShowPreviewModal(true);
  };

  // Export content
  const handleExport = (type, content) => {
    const title = type === 'terms_conditions' ? 'Terms & Conditions' : 'Privacy Policy';
    const blob = new Blob([content], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(' ', '_').toLowerCase()}_${new Date().toISOString().split('T')[0]}.html`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Auto-resize textarea
  const handleTextareaChange = (e, setter) => {
    setter(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 'px';
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-white rounded-3xl shadow-card p-6 mb-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Content Management</h2>
          <button
            onClick={fetchAllDocuments}
            disabled={loading}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FiRefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Status Messages */}
        {uploadSuccess && (
          <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
            {uploadSuccess}
          </div>
        )}
        {uploadError && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {uploadError}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Terms & Conditions */}
        <div className="bg-white rounded-3xl shadow-card p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-gray-800">Terms & Conditions</h3>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-500">
                Last updated: {formatDate(documentsInfo.terms_conditions.lastUpdated)}
              </span>
            </div>
          </div>

          <div className="mb-4">
            <textarea
              value={terms}
              onChange={(e) => handleTextareaChange(e, setTerms)}
              rows="12"
              className="w-full p-4 text-sm border rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none overflow-hidden"
              placeholder="Enter your Terms & Conditions content here. You can use HTML formatting..."
              style={{ minHeight: '300px' }}
            />
            <div className="mt-2 text-xs text-gray-500">
              Characters: {terms.length} | Supports HTML formatting
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex space-x-2">
              <button
                onClick={() => handleSave("terms_conditions", terms)}
                disabled={saveLoading.terms_conditions || !terms.trim()}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                  saveLoading.terms_conditions || !terms.trim()
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                <FaSave className="mr-2" />
                {saveLoading.terms_conditions ? "Saving..." : "Save Terms"}
              </button>
              
              <button
                onClick={() => handlePreview("terms_conditions", terms)}
                disabled={!terms.trim()}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <FaEye className="mr-2" />
                Preview
              </button>
            </div>

            <div className="flex space-x-2">
              <button
                onClick={() => handleExport("terms_conditions", terms)}
                disabled={!terms.trim()}
                className="flex items-center px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <FaDownload className="mr-2" />
                Export
              </button>
              
              <button
                onClick={() => setDeleteConfirm({ type: "terms_conditions", setter: setTerms, title: "Terms & Conditions" })}
                disabled={!terms.trim()}
                className="flex items-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <FaTrash className="mr-2" />
                Delete
              </button>
            </div>
          </div>
        </div>

        {/* Privacy Policy */}
        <div className="bg-white rounded-3xl shadow-card p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold text-gray-800">Privacy Policy</h3>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-500">
                Last updated: {formatDate(documentsInfo.privacy_policy.lastUpdated)}
              </span>
            </div>
          </div>

          <div className="mb-4">
            <textarea
              value={privacyPolicy}
              onChange={(e) => handleTextareaChange(e, setPrivacyPolicy)}
              rows="12"
              className="w-full p-4 text-sm border rounded-lg border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none overflow-hidden"
              placeholder="Enter your Privacy Policy content here. You can use HTML formatting..."
              style={{ minHeight: '300px' }}
            />
            <div className="mt-2 text-xs text-gray-500">
              Characters: {privacyPolicy.length} | Supports HTML formatting
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex space-x-2">
              <button
                onClick={() => handleSave("privacy_policy", privacyPolicy)}
                disabled={saveLoading.privacy_policy || !privacyPolicy.trim()}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                  saveLoading.privacy_policy || !privacyPolicy.trim()
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                <FaSave className="mr-2" />
                {saveLoading.privacy_policy ? "Saving..." : "Save Policy"}
              </button>
              
              <button
                onClick={() => handlePreview("privacy_policy", privacyPolicy)}
                disabled={!privacyPolicy.trim()}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <FaEye className="mr-2" />
                Preview
              </button>
            </div>

            <div className="flex space-x-2">
              <button
                onClick={() => handleExport("privacy_policy", privacyPolicy)}
                disabled={!privacyPolicy.trim()}
                className="flex items-center px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <FaDownload className="mr-2" />
                Export
              </button>
              
              <button
                onClick={() => setDeleteConfirm({ type: "privacy_policy", setter: setPrivacyPolicy, title: "Privacy Policy" })}
                disabled={!privacyPolicy.trim()}
                className="flex items-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <FaTrash className="mr-2" />
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Templates */}
      <div className="bg-white rounded-3xl shadow-card p-6 mt-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick HTML Templates</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Heading 1", code: "<h1>Your Heading</h1>" },
            { label: "Heading 2", code: "<h2>Your Subheading</h2>" },
            { label: "Paragraph", code: "<p>Your paragraph text here.</p>" },
            { label: "Bold Text", code: "<strong>Bold text</strong>" },
            { label: "Italic Text", code: "<em>Italic text</em>" },
            { label: "Unordered List", code: "<ul>\n  <li>Item 1</li>\n  <li>Item 2</li>\n</ul>" },
            { label: "Ordered List", code: "<ol>\n  <li>First item</li>\n  <li>Second item</li>\n</ol>" },
            { label: "Link", code: '<a href="https://example.com">Link text</a>' }
          ].map((template) => (
            <button
              key={template.label}
              onClick={() => {
                navigator.clipboard.writeText(template.code);
                setUploadSuccess(`${template.label} template copied to clipboard!`);
                setTimeout(() => setUploadSuccess(null), 2000);
              }}
              className="p-3 text-left bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
            >
              <div className="font-medium text-gray-800 mb-1">{template.label}</div>
              <div className="text-xs text-gray-600 font-mono">{template.code}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
              <h3 className="text-xl font-bold">{previewContent.title} - Preview</h3>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-full"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              <div 
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewContent.content) }}
              />
            </div>
            
            <div className="sticky bottom-0 bg-gray-50 p-4 border-t flex justify-end space-x-3">
              <button
                onClick={() => handleExport(previewContent.type, previewContent.content)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <FaDownload className="inline mr-2" />
                Export
              </button>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold">Confirm Deletion</h3>
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FaTimes className="text-lg" />
                </button>
              </div>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete "<strong>{deleteConfirm.title}</strong>"? This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm.type, deleteConfirm.setter)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentManagement;